// Meals.js - Updated to handle paginated response
import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  LinearProgress,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Restaurant as MealsIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Timeline as TimelineIcon,
  EmojiEvents as EmojiEventsIcon,
  SentimentSatisfiedAlt as SentimentSatisfiedAltIcon,
  SentimentDissatisfied as SentimentDissatisfiedIcon,
  SentimentVeryDissatisfied as SentimentVeryDissatisfiedIcon,
} from "@mui/icons-material";
import { useQueryClient, useQuery, useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { QK } from "../../../../../../base/qk";
import { useSnackbar } from "notistack";
import {
  getMealsByClientID,
  createMeal,
  deleteMeal,
} from "../../../../../../api/services/Observations/meal";
import { useUserProfile } from "../../../../../../hooks/userProfile";
import DataTable from "../../../../../Table/DataTable";
import ConfirmModal from "../../../../../ConfirmModal/ConfirmModal";
import EditMealModal from "./editMealModal";
import CustomCheckbox from "../../../../../CustomForm/CustomCheckbox";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import CustomDateTime from "../../../../../CustomForm/CustomDateTime";
import MealConsumed from "./MealConsumed";
import { MealOptions, getMealIcon, getMealColor } from "./meals.config";
import dayjs from "dayjs";


const Meals = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    type: "",
    notes: "",
    date_time: dayjs().format("YYYY-MM-DD HH:mm"),
    handover: false,
    manager: false,
    value: 0,
  });
  const queryClient = useQueryClient();
  const { id: clientID } = useParams();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [RowID, setRowID] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [showModal, setShowModal] = useState(false);
  const [selectedID, setSelectedID] = useState(null);

  const profile = useUserProfile();

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleValueChange = (event, value) => {
    setFormData((prev) => ({
      ...prev,
      value: value,
    }));
  };

  const handleCheckBox = (checked, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const resetForm = () => {
    setFormData({
      type: "",
      notes: "",
      date_time: dayjs().format("YYYY-MM-DD HH:mm"),
      handover: false,
      manager: false,
      value: 0,
    });
  };

  const { data: response, isLoading } = useQuery(
    [QK.meals, clientID],
    () => getMealsByClientID(clientID),
    {
      enabled: !!clientID,
    }
  );

  const assessments = response?.results || [];

  const createMutation = useMutation(createMeal, {
    onSuccess: () => {
      enqueueSnackbar("Meal entry created successfully.", {
        variant: "success",
      });
      queryClient.invalidateQueries([QK.meals, clientID]);
      resetForm();
    },
    onError: (error) => {
      enqueueSnackbar(error?.response?.data?.message || "Failed to create meal entry.", {
        variant: "error",
      });
    },
  });

  const deleteMutation = useMutation(deleteMeal, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.meals, clientID]);
      enqueueSnackbar("Meal entry deleted successfully", { variant: "success" });
      setShowConfirmModal(false);
    },
    onError: () => {
      enqueueSnackbar("Failed to delete meal entry", { variant: "error" });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.type) {
      enqueueSnackbar("Please select a meal type.", { variant: "warning" });
      return;
    }
    const updatedFormData = {
      ...formData,
      client: clientID,
      carer: profile?.id,
    };
    createMutation.mutate(updatedFormData);
  };

  const handleOpenModal = (id) => {
    setSelectedID(id);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedID(null);
  };

  const handleUpdate = (id) => {
    handleOpenModal(id);
  };

  const handleDelete = (ID) => {
    setRowID(ID);
    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    if (RowID) {
      deleteMutation.mutate(RowID);
    }
  };

  const getPercentageColor = (percentage) => {
    if (percentage >= 80) return "#22C55E";
    if (percentage >= 60) return "#3B82F6";
    if (percentage >= 40) return "#F59E0B";
    if (percentage >= 20) return "#F97316";
    return "#EF4444";
  };

  const getPercentageLabel = (percentage) => {
    if (percentage >= 80) return "Excellent";
    if (percentage >= 60) return "Good";
    if (percentage >= 40) return "Average";
    if (percentage >= 20) return "Poor";
    return "Very Poor";
  };

  const getPercentageIcon = (percentage) => {
    if (percentage >= 80) return <EmojiEventsIcon fontSize="small" />;
    if (percentage >= 60) return <SentimentSatisfiedAltIcon fontSize="small" />;
    if (percentage >= 40) return <SentimentDissatisfiedIcon fontSize="small" />;
    return <SentimentVeryDissatisfiedIcon fontSize="small" />;
  };

  const headers = [
    { key: "type", label: "Meal Type" },
    { key: "value", label: "Consumed" },
    { key: "notes", label: "Notes" },
    { key: "date_time", label: "Date & Time" },
    { key: "manager", label: "Manager" },
    { key: "handover", label: "Handover" },
  ];

  const renderCell = (item, header) => {
    if (header.key === "type") {
      const color = getMealColor(item.type);
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              bgcolor: alpha(color, 0.12),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: color,
            }}
          >
            {getMealIcon(item.type)}
          </Box>
          <Typography variant="body2" fontWeight={500} color="#1A1A2E">
            {item.type || "-"}
          </Typography>
        </Box>
      );
    }

    if (header.key === "value") {
      const percentage = item.value || 0;
      const color = getPercentageColor(percentage);
      return (
        <Box sx={{ minWidth: 160 }}>
          <Stack spacing={0.75}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {getPercentageIcon(percentage)}
                <Typography variant="caption" fontWeight={500} color="#667085">
                  {getPercentageLabel(percentage)}
                </Typography>
              </Box>
              <Typography variant="caption" fontWeight={700} color={color}>
                {percentage}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(color, 0.12),
                "& .MuiLinearProgress-bar": {
                  bgcolor: color,
                  borderRadius: 4,
                  transition: "width 0.6s ease",
                },
              }}
            />
          </Stack>
        </Box>
      );
    }

    if (header.key === "date_time") {
      if (!item.date_time) return "-";
      const date = new Date(item.date_time);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    if (header.key === "manager" || header.key === "handover") {
      const value = item[header.key];
      return value ? (
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
          label="Yes"
          size="small"
          sx={{
            height: 22,
            fontSize: "0.6rem",
            fontWeight: 600,
            bgcolor: "#D1FAE5",
            color: "#039855",
            "& .MuiChip-icon": { color: "#039855", fontSize: 12 },
          }}
        />
      ) : (
        <Chip
          icon={<CancelIcon sx={{ fontSize: 12 }} />}
          label="No"
          size="small"
          sx={{
            height: 22,
            fontSize: "0.6rem",
            fontWeight: 600,
            bgcolor: "#FEE2E2",
            color: "#DC2626",
            "& .MuiChip-icon": { color: "#DC2626", fontSize: 12 },
          }}
        />
      );
    }

    if (header.key === "notes") {
      return (
        <Typography
          variant="body2"
          color="#475467"
          sx={{
            maxWidth: 180,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.notes || "-"}
        </Typography>
      );
    }

    return item[header.key] || "-";
  };

  return (
    <Box>
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          mb: 3,
          border: "1px solid #F2F4F7",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#FAFBFC",
            borderBottom: "1px solid #F2F4F7",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MealsIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            </Box>
            <Typography variant="h6" fontWeight={600} color="#1A1A2E">
              Record Meal
            </Typography>
            <Chip
              label="New Entry"
              size="small"
              sx={{
                height: 22,
                fontSize: "0.6rem",
                fontWeight: 600,
                bgcolor: "#D1FAE5",
                color: "#039855",
              }}
            />
          </Box>
          <Typography variant="caption" color="#98A2B3">
            {dayjs().format("DD MMM YYYY, HH:mm")}
          </Typography>
        </Box>

        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
              <Box sx={{ flex: "1 1 220px", minWidth: "200px" }}>
                <FormControl fullWidth size="medium" required>
                  <InputLabel>Meal Type *</InputLabel>
                  <Select
                    name="type"
                    value={formData.type || ""}
                    onChange={handleChange}
                    label="Meal Type *"
                    sx={{
                      borderRadius: "8px",
                      minHeight: "52px",
                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                        py: 1,
                      },
                    }}
                  >
                    {MealOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          py: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 32,
                            height: 32,
                            borderRadius: "6px",
                            bgcolor: alpha(option.color, 0.12),
                            color: option.color,
                          }}
                        >
                          {option.icon}
                        </Box>
                        <Typography variant="body2" fontWeight={500}>
                          {option.label}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: "1 1 220px", minWidth: "200px" }}>
                <CustomDateTime
                  name="date_time"
                  label="Date & Time *"
                  onChange={handleChange}
                  value={formData.date_time || ""}
                  required
                  format="YYYY-MM-DD HH:mm"
                />
              </Box>

              <Box sx={{ flex: "1 1 220px", minWidth: "200px" }}>
                <CustomTextArea
                  label="Notes"
                  name="notes"
                  value={formData.notes || ""}
                  onChange={handleChange}
                  rows={1}
                  fullWidth
                  placeholder="Additional notes..."
                />
              </Box>

              <Box sx={{ width: "100%" }}>
                <MealConsumed
                  value={formData.value || 0}
                  onChange={handleValueChange}
                />
              </Box>

              <Box sx={{ width: "100%" }}>
                <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  <CustomCheckbox
                    label="Share with Manager"
                    name="manager"
                    checked={formData.manager || false}
                    onClick={handleCheckBox}
                  />
                  <CustomCheckbox
                    label="Handover"
                    name="handover"
                    checked={formData.handover || false}
                    onClick={handleCheckBox}
                  />
                </Box>
              </Box>

              <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end", gap: 2, mt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  disabled={createMutation.isLoading}
                  startIcon={<ClearIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: "8px",
                    borderColor: "#D0D5DD",
                    color: "#667085",
                    px: 3,
                    "&:hover": { bgcolor: "#F9FAFB", borderColor: "#98A2B3" },
                  }}
                >
                  Clear
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={createMutation.isLoading}
                  startIcon={createMutation.isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "8px",
                    bgcolor: "#2469F0",
                    px: 4,
                    "&:hover": {
                      bgcolor: "#1A5BD4",
                      boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
                    },
                  }}
                >
                  {createMutation.isLoading ? "Saving..." : "Save Meal Record"}
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>

      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          border: "1px solid #F2F4F7",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#FAFBFC",
            borderBottom: "1px solid #F2F4F7",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <TimelineIcon sx={{ color: "#667085", fontSize: 20 }} />
            <Typography variant="h6" fontWeight={600} color="#1A1A2E">
              Meal History
            </Typography>
            <Chip
              label={`${assessments.length || 0} records`}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.6rem",
                fontWeight: 500,
                bgcolor: "#F2F4F7",
                color: "#475467",
              }}
            />
          </Box>
        </Box>

        <CardContent sx={{ p: 0 }}>
          <DataTable
            data={assessments}
            headers={headers}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            canUpdate={true}
            canDelete={true}
            renderCell={renderCell}
            loading={isLoading}
          />
        </CardContent>
      </Card>

      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={handleConfirm}
        title="Delete Confirmation"
        body="Are you sure you want to delete this meal entry? This action cannot be undone."
        isDelete={true}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <EditMealModal
        show={showModal}
        handleClose={handleCloseModal}
        selectedID={selectedID}
      />
    </Box>
  );
};

export default Meals;